<template>
  <Dialog :visible="visible" header="Add fields from a template" modal :style="{ width: '40rem', maxWidth: '95vw' }"
    @update:visible="emit('update:visible', $event)">
    <p class="text-sm text-surface-500 mb-4">
      Templates add ready-made fields in one click. Fields you already have are left as they are, and you can rename,
      reorder or retire the new ones afterwards like any other field.
    </p>

    <div v-if="loading && !templates.length" class="py-6 text-center text-surface-500">Loading templates…</div>
    <div v-else-if="!templates.length" class="py-6 text-center text-surface-500" data-testid="custom-field-templates-empty">
      There are no templates for this record type yet.
    </div>

    <div v-else class="flex flex-col gap-3">
      <div v-for="template in templates" :key="template.key"
        class="border border-surface-200 dark:border-surface-700 rounded-border p-3 flex flex-col gap-2"
        :data-testid="`custom-field-template-${template.key}`">
        <div class="flex flex-wrap items-start justify-between gap-2">
          <div class="flex flex-col gap-0.5">
            <span class="font-semibold">{{ template.name }}</span>
            <span class="text-sm text-surface-500">{{ template.description }}</span>
          </div>
          <Button :label="templateApplyLabel(template)" :icon="template.applied ? 'pi pi-check' : 'pi pi-plus'" size="small"
            :severity="template.applied ? 'secondary' : undefined" :outlined="template.applied"
            :disabled="template.applied || applyingKey !== null" :loading="applyingKey === template.key"
            :data-testid="`btn-apply-template-${template.key}`" @click="apply(template.key)" />
        </div>
        <ul class="flex flex-col gap-1 text-sm">
          <li v-for="field in template.fields" :key="field.key" class="flex flex-wrap items-center gap-2">
            <span class="font-medium">{{ field.label }}</span>
            <span class="text-surface-500">{{ customFieldKindLabel(field, template.fields) }}</span>
            <Tag v-if="field.exists" value="Already added" severity="secondary" class="text-xs" />
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <Button label="Close" severity="secondary" text @click="emit('update:visible', false)" data-testid="btn-templates-close" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import type { CustomFieldEntity } from '@/types/composition-functions';
import { useCustomFieldTemplates } from '@/composables/queries/useCustomFieldTemplates';
import { useCustomFieldMutations } from '@/composables/mutations/useCustomFieldMutations';
import { customFieldKindLabel, templateApplyLabel, templateAppliedMessage } from '@/utils/customFields';

/** Ready-made custom field sets a school adds in one click (#574), e.g. state of origin and LGA. */
const props = defineProps<{
  visible: boolean;
  entity: CustomFieldEntity;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'applied'): void;
}>();

const toast = useToast();
const { templates, loading, refetch } = useCustomFieldTemplates({
  entity: computed(() => props.entity),
  enabled: computed(() => props.visible),
});
const { applyTemplate } = useCustomFieldMutations();
const applyingKey = ref<string | null>(null);

async function apply(key: string) {
  applyingKey.value = key;
  try {
    const result = await applyTemplate.mutate({ entity: props.entity, template: key });
    toast.add({ severity: 'success', summary: 'Template added', detail: templateAppliedMessage(result?.data?.applyCustomFieldTemplate ?? []), life: 4000 });
    emit('applied');
    await refetch();
  } catch (error) {
    console.error(error);
    toast.add({ severity: 'error', summary: 'Could not add the template', detail: (error as Error)?.message || 'Please try again.', life: 5000 });
  } finally {
    applyingKey.value = null;
  }
}
</script>
